import { useMediaQuery, useTheme } from '@material-ui/core';
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { DisplayItem } from 'design-system';
import { FormEvent, MouseEventHandler, useEffect, useState } from 'react';
import { ProfileScreenPropsRedux } from 'src/components/screens/ProfileScreen/ProfileScreen';
import {
  CREATE_PERSONAL_AGENT_LINK,
  UPDATE_PERSONAL_AGENT_LINK,
} from 'src/store/personalAgentLink/requestIdentifiers';
import { REQUEST_SUBAGENTS } from 'src/store/quotation/requestIdentifiers';

interface UseProfileProps {
  isMobile: boolean;
  subAgentList: DisplayItem<object>[];
  handleSubAgentChange: (val: DisplayItem<object>) => void;
  selectSubAgent: string;
  handleUserLinkChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  userLink?: string | null;
  handleFormSubmit(e: FormEvent): void;
  disabledButton: boolean;
  enableIcons: boolean;
  handleEnableEdit: () => void;
  disabledDropdown: boolean;
  networkStatus: RequestIndicatorTypes;
  loadingSubmit: boolean;
  baseLink: string;
  handleCopyLink: MouseEventHandler<SVGSVGElement>;
  anchorEl: SVGSVGElement | null;
  openModal: boolean;
  handleCloseModal: () => void;
  error: boolean;
  message: string;
  enableEdit: boolean;
}

const initMessage =
  'Este link lo puedes colocar en tu sitio web o enviarlo a tus clientes.';

const calculatorBaseLink = process.env.REACT_APP_CALCULATOR_URL as string;

const useProfile = (props: ProfileScreenPropsRedux): UseProfileProps => {
  const {
    getLicenses,
    getSubagents,
    licenses,
    subAgentList,
    getPersonalAgentLink,
  } = props;

  const { agentCode, brokerLicense, personalLink } = props.personalLink;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [selectSubAgent, setSelectSubAgent] = useState('');
  const [error, setError] = useState(false);
  const [message, setMessage] = useState<string>(initMessage);
  const [userLink, setUserLink] = useState<string | null | undefined>(null);
  const [enableIcons, setEnableIcons] = useState(false);
  const statusSubagents = useNetworkObserver(REQUEST_SUBAGENTS);
  const statusCreate = useNetworkObserver(CREATE_PERSONAL_AGENT_LINK);
  const statusUpdate = useNetworkObserver(UPDATE_PERSONAL_AGENT_LINK);
  const [enableEdit, setEnableEdit] = useState(false);
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const baseLink = 'calculadora.assanet.cr/';

  const handleSubAgentChange = (val: DisplayItem<object>) => {
    props.getPersonalAgentLink({
      agentCode: val.Value,
      brokerLicense: props.licenses[0].code,
    });
    setUserLink('');
    setSelectSubAgent(val.Value);
    setEnableEdit(false);
    setEnableIcons(false);
    setError(false);
    setMessage(initMessage);
  };

  const handleEnableEdit = () => setEnableEdit(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleUserLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (enableIcons && enableEdit) setUserLink(value);
    if (!personalLink) setUserLink(value);
    const regex = /^[a-zA-Z0-9-_]*$/;
    if (value.length < 10) {
      setError(true);
      setMessage(
        'El URL personalizado debe de tener un mínimo de 10 caracteres',
      );
      return;
    }
    if (value.length > 20) {
      setError(true);
      setMessage(
        'El URL personalizado debe de tener un máximo de 20 caracteres',
      );
      return;
    }
    if (!regex.test(value)) {
      setError(true);
      setMessage(
        'Caracteres no válidos. Utiliza únicamente números, letras o guiones.',
      );
      return;
    }
    setError(false);
    setMessage(initMessage);
  };

  const handleCopyLink: MouseEventHandler<SVGSVGElement> = (e) => {
    navigator.clipboard.writeText(`${calculatorBaseLink}/${userLink}`);
    setAnchorEl(e.currentTarget);
  };

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    const enableSubmit = userLink && agentCode && brokerLicense;
    if (personalLink && enableEdit && enableSubmit) {
      props.updatePersonalAgentLink({
        agentCode,
        brokerLicense,
        personalLink: userLink,
      });
    }
    if (!personalLink && userLink) {
      props.createPersonalAgentLink({
        agentCode: selectSubAgent || props.licenses[0].code,
        brokerLicense: props.licenses[0].code,
        personalLink: userLink,
      });
    }
  };

  const handleErrorOrModal = (status: RequestIndicatorTypes) => {
    if (status === 'failure') {
      setMessage('El URL ingresado ya se encuentra en uso, prueba con otro');
      setError(true);
      setOpenModal(false);
    }
    if (status === 'success') setOpenModal(true);
  };

  useEffect(() => {
    if (licenses.length === 0) {
      getLicenses();
    }
  }, [getLicenses, licenses.length, statusSubagents]);

  useEffect(() => {
    if (licenses.length > 0 && statusSubagents === 'initial') {
      getSubagents(licenses[0].code);
    }
  }, [getSubagents, licenses, statusSubagents, subAgentList.length]);

  useEffect(() => {
    if (personalLink) {
      setUserLink(personalLink);
      setEnableIcons(!!personalLink);
    }
  }, [personalLink]);

  useEffect(() => {
    if (
      statusSubagents === 'success' &&
      subAgentList.length === 0 &&
      licenses.length > 0
    ) {
      getPersonalAgentLink({
        agentCode: licenses[0].code,
        brokerLicense: licenses[0].code,
      });
    }
  }, [
    getPersonalAgentLink,
    licenses,
    licenses.length,
    statusSubagents,
    subAgentList.length,
  ]);

  useEffect(() => {
    if (anchorEl) {
      setTimeout(() => {
        setAnchorEl(null);
      }, 3000);
    }
  }, [anchorEl]);

  useEffect(() => {
    handleErrorOrModal(statusCreate);
  }, [statusCreate]);

  useEffect(() => {
    handleErrorOrModal(statusUpdate);
  }, [statusUpdate]);

  return {
    isMobile,
    subAgentList: subAgentList.map((subAgent) => ({
      Value: subAgent.code,
      Text: `${subAgent.name} - ${subAgent.license}`,
    })),
    handleSubAgentChange,
    selectSubAgent,
    handleUserLinkChange,
    userLink,
    handleFormSubmit,
    enableIcons,
    handleEnableEdit,
    disabledDropdown:
      statusSubagents === 'success' && subAgentList.length === 0,
    disabledButton:
      !userLink ||
      userLink?.length === 0 ||
      (selectSubAgent.length === 0 && subAgentList.length > 0) ||
      (!!personalLink && !enableEdit) ||
      (!!personalLink && enableEdit && personalLink === userLink),
    networkStatus: statusSubagents,
    loadingSubmit:
      statusCreate === 'requesting' || statusUpdate === 'requesting',
    baseLink,
    handleCopyLink,
    anchorEl,
    openModal,
    handleCloseModal,
    error,
    message,
    enableEdit,
  };
};

export default useProfile;
